import React, { useEffect, useMemo } from "react";
import {
  Button,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import schema from "./schema";
import type { EmployeeBankAccount } from "../types/employee-bank-account.types";
import {
  createEmployeeBankAccount,
  updateEmployeeBankAccount,
} from "../services/employeeBankAccountService";

const BankAccountFormModal = ({
  isOpen,
  onClose,
  employeeId,
  editRecord,
  onSaved,
}: {
  isOpen: boolean;
  onClose: () => void;
  employeeId: number;
  editRecord: EmployeeBankAccount | null;
  onSaved: () => void;
}) => {
  const isEdit = !!editRecord;

  const initialValues = useMemo(
    () => ({
      accountHolderName: editRecord?.accountHolderName ?? "",
      bankName: editRecord?.bankName ?? "",
      branchName: editRecord?.branchName ?? "",
      accountNumber: editRecord?.accountNumber ?? "",
      ifscCode: editRecord?.ifscCode ?? "",
      isPrimary: editRecord?.isPrimary ?? false,
    }),
    [editRecord],
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: schema,
    onSubmit: async (values) => {
      const branchName =
        values.branchName != null && String(values.branchName).trim() !== ""
          ? String(values.branchName).trim()
          : null;

      try {
        if (isEdit && editRecord) {
          const res = await updateEmployeeBankAccount(
            employeeId,
            editRecord.id,
            {
              accountHolderName: values.accountHolderName.trim(),
              bankName: values.bankName.trim(),
              branchName,
              accountNumber: values.accountNumber.trim(),
              ifscCode: values.ifscCode.trim().toUpperCase(),
              isPrimary: values.isPrimary,
            },
          );
          if (res.success) {
            toast.success(res.message);
            onSaved();
            onClose();
          }
        } else {
          const res = await createEmployeeBankAccount(employeeId, {
            accountHolderName: values.accountHolderName.trim(),
            bankName: values.bankName.trim(),
            branchName,
            accountNumber: values.accountNumber.trim(),
            ifscCode: values.ifscCode.trim().toUpperCase(),
            isPrimary: values.isPrimary,
          });
          if (res.success) {
            toast.success(res.message);
            onSaved();
            onClose();
          }
        }
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message ??
            (isEdit ? "Failed to update bank account" : "Failed to add bank account"),
        );
      }
    },
  });

  useEffect(() => {
    if (!isOpen) {
      validation.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    validation.resetForm();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered scrollable>
      <ModalHeader toggle={handleClose}>
        {isEdit ? "Edit bank account" : "Add bank account"}
      </ModalHeader>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
        }}
      >
        <ModalBody>
          <Row className="gy-3">
            <Col md={12}>
              <Label className="form-label">Account holder name</Label>
              <Input
                type="text"
                name="accountHolderName"
                value={validation.values.accountHolderName}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  !!(
                    validation.touched.accountHolderName &&
                    validation.errors.accountHolderName
                  )
                }
              />
              {validation.touched.accountHolderName &&
                validation.errors.accountHolderName && (
                  <FormFeedback type="invalid">
                    {validation.errors.accountHolderName}
                  </FormFeedback>
                )}
            </Col>
            <Col md={6}>
              <Label className="form-label">Bank name</Label>
              <Input
                type="text"
                name="bankName"
                value={validation.values.bankName}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  !!(validation.touched.bankName && validation.errors.bankName)
                }
              />
              {validation.touched.bankName && validation.errors.bankName && (
                <FormFeedback type="invalid">
                  {validation.errors.bankName}
                </FormFeedback>
              )}
            </Col>
            <Col md={6}>
              <Label className="form-label">Branch name</Label>
              <Input
                type="text"
                name="branchName"
                placeholder="Optional"
                value={validation.values.branchName || ""}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
              />
            </Col>
            <Col md={6}>
              <Label className="form-label">Account number</Label>
              <Input
                type="text"
                name="accountNumber"
                autoComplete="off"
                value={validation.values.accountNumber}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  !!(
                    validation.touched.accountNumber &&
                    validation.errors.accountNumber
                  )
                }
              />
              {validation.touched.accountNumber &&
                validation.errors.accountNumber && (
                  <FormFeedback type="invalid">
                    {validation.errors.accountNumber}
                  </FormFeedback>
                )}
            </Col>
            <Col md={6}>
              <Label className="form-label">IFSC code</Label>
              <Input
                type="text"
                name="ifscCode"
                className="text-uppercase font-monospace"
                placeholder="11 characters, e.g. SBIN0001234"
                maxLength={11}
                autoComplete="off"
                value={validation.values.ifscCode}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  !!(validation.touched.ifscCode && validation.errors.ifscCode)
                }
              />
              {validation.touched.ifscCode && validation.errors.ifscCode && (
                <FormFeedback type="invalid">
                  {validation.errors.ifscCode}
                </FormFeedback>
              )}
            </Col>
            <Col md={12}>
              <div className="form-check">
                <Input
                  type="checkbox"
                  id="bank-account-primary"
                  name="isPrimary"
                  className="form-check-input"
                  checked={validation.values.isPrimary}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                />
                <Label
                  className="form-check-label"
                  htmlFor="bank-account-primary"
                >
                  Set as primary account
                </Label>
              </div>
              <p className="text-muted small mb-0 mt-1">
                Marking this primary will unset other accounts for this employee.
              </p>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button type="button" color="light" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            disabled={validation.isSubmitting}
          >
            {validation.isSubmitting
              ? "Saving..."
              : isEdit
                ? "Save changes"
                : "Add account"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default BankAccountFormModal;
